// Visual constants
export const VISUAL = {
  GRID_COLOR: 0x111122,
  GRID_SPACING: 40,
  AIM_LINE_BASE: 30,
  AIM_LINE_POWER_MULT: 2,
} as const;

// Tank visual model
export const TANK_VISUAL = {
  SIDES: 6,
  BODY_RADIUS: 35,
  TURRET_LENGTH: 56,
  TURRET_WIDTH: 6,
  TIP_RADIUS: 6,
} as const;

// Aim sweep trail + sparks (Sakurai blade trail — trailing angle, gradient slices, speed scaling)
// Co-located with TANK_VISUAL due to definition-time cross-reference (TRAIL_OUTER_RADIUS)
export const AIM_FX = {
  TRAIL_MIN_DELTA: 0.008,
  TRAIL_CHASE_SPEED: 5,
  TRAIL_FADE_SPEED: 20,
  TRAIL_SLICES: 4,
  TRAIL_BRIGHT_ALPHA: 0.55,
  TRAIL_DARK_ALPHA: 0.5,
  TRAIL_DARK_COLOR: 0x050520,
  TRAIL_HIGHLIGHT_ALPHA: 0.6,
  TRAIL_HIGHLIGHT_INSET: 3,
  TRAIL_EDGE_ALPHA: 0.8,
  TRAIL_EDGE_WIDTH: 2.5,
  TRAIL_INNER_RADIUS: 10,
  TRAIL_OUTER_RADIUS: TANK_VISUAL.TURRET_LENGTH + TANK_VISUAL.TIP_RADIUS + 6,
  TRAIL_SPEED_ALPHA_MIN: 0.5,
  TRAIL_SPEED_ALPHA_MAX: 1.0,
  TRAIL_SPEED_RADIUS_BONUS: 5,
  SPARK_THRESHOLD: 0.025,
  SPARK_COOLDOWN_MIN: 2,
  SPARK_COOLDOWN_MAX: 3,
  SPARK_COUNT_MIN: 2,
  SPARK_COUNT_MAX: 3,
  SPARK_SPEED_MIN: 1.5,
  SPARK_SPEED_MAX: 4.0,
  SPARK_LIFE: 8,
  SPARK_SCALE_MIN: 0.25,
  SPARK_SCALE_MAX: 0.4,
  SPARK_DARK_CHANCE: 0.3,
  SPARK_DARK_COLOR: 0x1a1a2e,
  START_SQUASH_BASE: 0.10,
  START_SQUASH_JITTER: 0.3,
  REVERSAL_SQUASH_BASE: 0.12,
  REVERSAL_SQUASH_SCALE: 3.0,
  REVERSAL_SQUASH_MAX: 0.35,
  REVERSAL_SQUASH_JITTER: 0.25,
} as const;

// Player colors (hex numbers for PixiJS)
export const PLAYER_COLORS = [
  0x00ffff, // Cyan - P1
  0xff00ff, // Magenta - P2
  0x44ff44, // Green - P3
  0xffaa00, // Orange - P4
] as const;

// Circuit board overlay (fill depth gradient + crater glow — traces/nodes/pulses/contours removed)
export const CIRCUIT = {
  // Fill depth gradient (grid cells from surface)
  FILL_SURFACE_DEPTH: 5,
  FILL_MID_DEPTH: 20,
  FILL_DEEP_DEPTH: 50,
  FILL_SURFACE_RGB: [10, 12, 30] as readonly number[],
  FILL_STANDARD_RGB: [5, 5, 16] as readonly number[],
  FILL_MID_RGB: [3, 3, 12] as readonly number[],
  FILL_DEEP_RGB: [2, 2, 8] as readonly number[],

  // Crater glow
  CRATER_HEAT_DECAY: 0.98,
  CRATER_REMOVE_THRESHOLD: 0.05,
  CRATER_RENDER_THRESHOLD: 0.2,
  CRATER_MAX_RENDERED: 5,
  CRATER_HOT_COLOR: 0xff4400,
  CRATER_COOL_COLOR: 0xff8800,
  CRATER_WIDTH_BASE: 3,
  CRATER_WIDTH_HEAT: 3,
  CRATER_ALPHA_MULT: 0.35,
  CRATER_GLOW_DISTANCE: 12,
  CRATER_GLOW_STRENGTH: 1.5,
} as const;

// Z-index layers
export const Z = {
  BG_SKY: -4,
  BG_DEEP: -3,
  BG_MID: -2,
  BACKGROUND_STARS: -1,
  BACKGROUND_GRID: 0,
  TERRAIN_FILL: 1,
  TERRAIN_EDGE: 2,
  VOID: 3,          // after terrain, before wind/tanks
  BUMPERS: 4,       // between void and wind — neon world edge lines
  WIND_EFFECTS: 5,  // above terrain, below particles/tanks (Sakurai display priority)
  PARTICLES: 10,
  PROJECTILES: 20,
  TARGETS: 25,      // targets above particles, below tanks
  TANKS: 30,        // Sakurai: characters above particles for readability
  DI_OVERLAY: 35,   // DI visuals sit above tanks for readability during window
  FLASH: 100,
  MENU_UI: 150,      // menu elements render above all game content
  HUD_GLOW: 199,
  HUD_BASE: 200,
  HUD_TICKS: 201,
  HUD_OVERLAY: 202,
  HUD_DIALOG: 210,
  HUD_DIALOG_BORDER: 211,
  HUD_DIALOG_TEXT: 212,
} as const;

// Gameplay action keys — custom virtual key codes that no physical keyboard key maps to.
// Mouse, gamepad, and touch inject into these; physical Shift/Space are inert during gameplay.
export const ACTION = {
  FIRE: 'GameFire',
  CHARGE: 'GameCharge',
  SPECIAL: 'GameSpecial',
  CANCEL: 'GameCancel',
  JUMP: 'GameJump',
  SWAP: 'GameSwap',
} as const;
